
/**
 * AssetCategory Service Layer
 * Rule: Business logic, validation, and API orchestration
 * Rule: NO UI IN SDK
 */
import { CreateAssetCategorySchema, UpdateAssetCategorySchema } from '@repo/contracts';
import { ApiError } from '../errors/api-error.js';

export class AssetCategoryService {
    constructor(api) {
        this.api = api;
    }

    /**
     * Get all items with pagination
     * @param {Object} params - Query parameters
     * @returns {Promise<Object>}
     */
    async getAll(params = {}) {
        try {
            const response = await this.api.getAll(params);
            return {
                success: true,
                data: response.data,
                meta: response.pagination || response.meta || {},
                uniqueValues: response.uniqueValues || {},
                message: response.message || 'AssetCategory listesi getirildi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch asset-category list');
        }
    }

    /**
     * Get item by ID
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async getById(id) {
        try {
            const response = await this.api.getById(id);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to fetch asset-category');
        }
    }

    /**
     * Create new item
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async create(data) {
        const validation = CreateAssetCategorySchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation('Invalid data', validation.error);
        }

        try {
            const response = await this.api.create(validation.data);
            return {
                success: true,
                data: response.data,
                message: response.message || 'AssetCategory oluşturuldu'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to create asset-category');
        }
    }

    /**
     * Update item
     * @param {number|string} id
     * @param {Object} data
     * @returns {Promise<Object>}
     */
    async update(id, data) {
        const validation = UpdateAssetCategorySchema.safeParse(data);
        if (!validation.success) {
            throw ApiError.validation('Invalid data', validation.error);
        }

        try {
            const response = await this.api.update(id, validation.data);
            return {
                success: true,
                data: response.data,
                message: response.message || 'AssetCategory güncellendi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to update asset-category');
        }
    }

    /**
     * Delete item
     * @param {number|string} id
     * @returns {Promise<Object>}
     */
    async delete(id) {
        try {
            const response = await this.api.delete(id);

            return {
                success: true,
                data: response.data,
                message: response.message || 'AssetCategory silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to delete asset-category');
        }
    }

    /**
     * Bulk delete items
     * @param {Array<number|string>} ids
     * @returns {Promise<Object>}
     */
    async bulkDelete(ids) {
        try {
            const response = await this.api.batchDelete(ids);
            return {
                success: true,
                data: response.data,
                deletedCount: response.deletedCount || ids.length,
                message: response.message || 'AssetCategory öğeleri silindi'
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to bulk delete asset-categories');
        }
    }

    /**
     * Custom operation: createBatchDelete
     */
    async createBatchDelete(data = {}) {
        try {
            const response = await this.api.client.post(`/batch-delete`, data);
            return {
                success: true,
                data: response.data,
                message: response.message
            };
        } catch (error) {
            throw this._handleError(error, 'Failed to execute createBatchDelete');
        }
    }

    /**
     * Handle and transform errors
     */
    _handleError(error, defaultMessage) {
        if (error instanceof ApiError) {
            throw error;
        }

        if (error.statusCode || error.status) {
            throw ApiError.fromResponse(error);
        }

        throw new ApiError(error.message || defaultMessage, 500, 'UNKNOWN_ERROR', error);
    }
}
